import React, { useEffect } from 'react'
import ReactDOM from 'react-dom/client'
import { createRouter, RouterProvider, useRouterState } from '@tanstack/react-router'
import { routeTree } from './routeTree.gen'
import './i18n'
import { AuthProvider, useAuth } from './auth'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ConfigProvider, notification } from 'antd'
import { canvas, colorPrimary, ink, inkQuaternary, inkSecondary, inkTertiary } from './branding'
import { AxiosError, isAxiosError } from 'axios'
import dayjs from 'dayjs'
import nlNl from 'antd/locale/nl_NL'
import enUs from 'antd/locale/en_US'

import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { Locale as AntdLocale } from 'antd/es/locale'
import { Locale } from './user/locale'
import * as Sentry from '@sentry/react'
import { useTranslation } from 'react-i18next'
import '@ant-design/v5-patch-for-react-19'

dayjs.extend(utc)
dayjs.extend(timezone)

// Set up a Router instance
const router = createRouter({
    routeTree,
    defaultPreload: 'intent',
    context: {
        auth: undefined!,
    },
})

// Register things for typesafety
declare module '@tanstack/react-router' {
    interface Register {
        defaultError: AxiosError
        router: typeof router
    }
}

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 60_000,
            retry: (failureCount, error) => {
                if (isAxiosError(error) && error.response?.status === 404) {
                    return false
                }

                return failureCount <= 3
            },
        },
    },
})

notification.config({
    placement: 'top',
    duration: 5,
})

const localMap: Record<Locale, AntdLocale> = {
    nl_NL: nlNl,
    en_US: enUs,
}

function InnerApp() {
    const auth = useAuth()
    const { t } = useTranslation()
    const onApplyForm = useRouterState({ router, select: (state) => state.location.pathname.startsWith('/apply/') })
    // Only for signed-in customers: on the sign-up and sign-in forms the button covers fields on a phone.
    const showFeedback = !onApplyForm && !!auth.user

    useEffect(() => {
        Sentry.init({
            dsn: 'https://74cb91639577421d26f21ca24d251692@o4507782361251840.ingest.de.sentry.io/4507793438867536',
            integrations: [
                Sentry.browserTracingIntegration(),
                Sentry.replayIntegration(),
                Sentry.feedbackIntegration({
                    autoInject: false,
                    colorScheme: 'light',
                    triggerLabel: t('Feedback'),
                    formTitle: t('Give Feedback'),
                    submitButtonLabel: t('Submit Feedback'),
                    accentBackground: colorPrimary,
                    showName: false,
                    showEmail: false,
                    showBranding: false,
                    messagePlaceholder: '',
                }),
            ],
            tracesSampleRate: 1.0,
            tracePropagationTargets: ['localhost'],
            replaysSessionSampleRate: 1.0,
            replaysOnErrorSampleRate: 1.0,
        })
        // should never be called multiple times
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (!showFeedback) return

        const feedback = Sentry.getFeedback()
        if (!feedback) return

        const widget = feedback.createWidget()

        return () => {
            widget.removeFromDom()
            feedback.remove()
        }
    }, [showFeedback])

    return (
        <ConfigProvider
            locale={localMap[auth.user?.locale || 'nl_NL']}
            theme={{
                // algorithm: theme.darkAlgorithm,
                token: {
                    fontWeightStrong: 400,
                    fontFamily: 'Poppins',
                    colorPrimary: colorPrimary,
                    colorLink: colorPrimary,
                    borderRadius: 12,
                    colorText: ink,
                    colorTextSecondary: inkSecondary,
                    colorTextTertiary: inkTertiary,
                    colorTextQuaternary: inkQuaternary,
                    colorBgLayout: canvas,
                },
            }}
        >
            <RouterProvider
                router={router}
                context={{ auth }}
            />
        </ConfigProvider>
    )
}

function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <AuthProvider>
                <InnerApp />
            </AuthProvider>
        </QueryClientProvider>
    )
}

const rootElement = document.getElementById('app')!

if (!rootElement.innerHTML) {
    const root = ReactDOM.createRoot(rootElement)
    root.render(<App />)
}
